<template>
    <div class="position-relative input-container" id="dropdown-container">
        <input
            id="autocomplete"
            type="text"
            @keyup="delayInput"
            ref="autocomplete"
            :value="props.store.data.selected.autocompleteValue"
            class="dropdown border-default bg-primary-white start-date-dropdown w-100 outline-none caption"
        />
        <div class="label-blue label" style="z-index: 1">{{ autocompleteLabel }}</div>
        <div
            id="input-overlay"
            class="overlay position-absolute border-default align-items-center cursor-pointer text-primary-black"
            ref="overlay"
            :class="{
                'subcaption-strong': props.store.data.selected.autocompleteValue,
                subcaption: !props.store.data.selected.autocompleteValue
            }"
        >
            {{
                props.store.data.selected.autocompleteValue
                    ? props.store.data.selected.autocompleteValue.label
                    : autocompleteLabel
            }}
        </div>
        <div class="icon-container position-absolute">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M9.1179 0.900024C5.5738 0.900024 2.69995 3.77387 2.69995 7.31798C2.69995 9.07599 3.40596 10.668 4.55116 11.8273C5.71423 13.0057 7.42756 15.2309 9.1179 17.1C10.7993 15.2309 12.5675 12.9852 13.7344 11.7762C14.849 10.6221 15.5359 9.05046 15.5359 7.31798C15.5359 3.77387 12.662 0.900024 9.1179 0.900024ZM9.1179 9.08876C7.94845 9.08876 7.00114 8.14145 7.00114 6.97199C7.00114 5.80254 7.94845 4.85523 9.1179 4.85523C10.2874 4.85523 11.2347 5.80254 11.2347 6.97199C11.2347 8.14145 10.2874 9.08876 9.1179 9.08876Z"
                    fill="#585F66"
                />
            </svg>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, Ref, computed, ComputedRef } from 'vue'
// Types
import type { IConstructedRegionCountry } from '../../../classes/RegionData'
import { ICountry } from '../../../classes/StationData'
import type { IDomain } from '../../../classes/DomainData'

// Api
import getAcoommodations from '../../../api/getAcoommodations'

import {AutocompleteState} from '../../../state/autocompleteState'

// Utils
import replaceAccents from '../../../utils/replaceAccents'

// Props
const props = defineProps<{
    originalDomains: IDomain[]
    originalRegions: IConstructedRegionCountry[]
    originalStations: ICountry[]
    contents: any
    store: any
    state: AutocompleteState
}>()

// Data
const inputValue: Ref<string> = ref('')
let typingTimer: number

// Element refs
const overlay = ref(null)
const autocomplete: Ref<HTMLInputElement | null> = ref(null)

// Copmuteds
const autocompleteLabel: ComputedRef<string> = computed(() => {
    return  props.store.state.summer ? props.contents.placeHolders.autocompleteSummer : props.contents.placeHolders.autocomplete
})

// Methods
const delayInput = (searchEvent: KeyboardEvent) => {
    const doneTypingInterval = 300

    clearTimeout(typingTimer)
    if (searchEvent.code === 'Space') {
        return
    }
    const target = searchEvent.target as HTMLInputElement
    inputValue.value = target.value
    typingTimer = setTimeout(handleInput, doneTypingInterval)
}

const handleInput = async (): Promise<void> => {
    const searchText = inputValue.value
    props.state.inputValue = searchText
    // Only execute the search after 3 chars
    if (searchText.length < 3) {
        props.state.showTopStations = true
        return
    } else {
        props.state.showTopStations = false
    }

    const originalDomains = props.originalDomains
    const originalRegions = props.originalRegions
    const originalStations = props.originalStations
    props.state.domains = searchItemsByLabel(originalDomains, searchText)
    props.state.regions = originalRegions.map((region) => {
        return {
            label: region.label,
            regions: searchItemsByLabel(region.regions, searchText)
        }
    })

    props.state.stations = originalStations.map((station) => {
        const filteredStations = searchItemsByLabel(station.items, searchText)
        return {
            ...station,
            items: filteredStations
        }
    })

    props.state.accommodations = !props.store.isCosog ? await getAcoommodations(searchText, props.store.state.locale) : []
}

const searchItemsByLabel = (items: any, searchText: string) => {
    const cleanedSearchText = replaceAccents(searchText)
    const escapedText = cleanedSearchText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const regex = new RegExp(escapedText, 'i')
    const arr: any = []
    const filteredItems = items.filter((item: any) => {
        const result = regex.test(replaceAccents(item.label))
        if (!result && item.stations) {
            const stations = item.stations.filter((station: any) => {
                return regex.test(replaceAccents(station.label))
            })
            arr.push(stations)
        }

        return result
    })

    return [...filteredItems, ...arr.flat()]
}

onMounted(() => {
    if (!overlay.value) return
    if (!autocomplete.value) return
    const overlayElement = overlay.value as HTMLElement
    const autocompleteElement = autocomplete.value as HTMLInputElement
    overlayElement.addEventListener('click', (e) => {
        e.stopPropagation()
        props.state.isDropdownOpen = true
        autocompleteElement.focus()
        overlayElement.style.display = 'none'
    })

    window.addEventListener('click', (e) => {
        if (e.target !== overlay.value && e.target !== autocomplete.value) {
            overlayElement.style.display = 'flex'
            props.state.isDropdownOpen = false
            const autocompleteEl: HTMLInputElement | null = autocomplete.value
            if (autocompleteEl) {
                autocompleteEl.value = ''
                props.state.showTopStations = true
            }
        }
    })
})
</script>

<style scoped>
.label {
    display: none;
}
#autocomplete:focus + .overlay {
    display: none;
}

#autocomplete:focus + .label {
    display: block;
}

#autocomplete:focus {
    box-shadow: inset 0 0 0 1px var(--primary-blue) !important;
}

#autocomplete {
    padding-left: 40px;
}
.overlay {
    top: 0;
    left: 0;
    right: 0;
    padding-left: 32px;

    bottom: 0;
    background-color: white;
    border-radius: 8px;
    display: flex;
}

.icon-container {
    top: 25%;
    left: 1%;
}
</style>
